import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Plus, Loader2 } from "lucide-react";
import { adminCategoriesQuery, type AdminCategory } from "@/lib/admin/categories.functions";

export const Route = createFileRoute("/admin/categorias/")({
  component: AdminCategoriesList,
});

function AdminCategoriesList() {
  const { data: categories, isLoading } = useQuery(adminCategoriesQuery());

  const topLevel = categories?.filter((c) => !c.parent_id) ?? [];
  const childrenOf = (parentId: string) =>
    categories?.filter((c) => c.parent_id === parentId) ?? [];

  const rows: { category: AdminCategory; isChild: boolean }[] = [];
  for (const top of topLevel) {
    rows.push({ category: top, isChild: false });
    for (const child of childrenOf(top.id)) {
      rows.push({ category: child, isChild: true });
    }
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold text-eg-dark">Categorias</h1>
        <Link
          to="/admin/categorias/novo"
          className="inline-flex items-center gap-2 rounded-md bg-eg-gold px-4 py-2 text-sm font-semibold text-eg-dark hover:bg-eg-goldhover transition-colors"
        >
          <Plus className="h-4 w-4" />
          Nova categoria
        </Link>
      </div>

      {isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-eg-gold" />
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
              <th className="px-4 py-3">Nome</th>
              <th className="px-4 py-3">Slug</th>
              <th className="px-4 py-3">Categoria no Tiny</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ category, isChild }) => (
              <tr key={category.id} className="border-b border-border last:border-0">
                <td className="px-4 py-3">
                  <Link
                    to="/admin/categorias/$id"
                    params={{ id: category.id }}
                    className="font-semibold text-eg-dark hover:text-eg-goldtext"
                  >
                    {isChild ? `— ${category.name}` : category.name}
                  </Link>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                  {category.slug}
                </td>
                <td className="px-4 py-3">
                  {category.tiny_category_id ? (
                    <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-700">
                      Vinculada
                    </span>
                  ) : (
                    <span className="rounded-full bg-muted px-2 py-1 text-xs font-semibold text-muted-foreground">
                      Não vinculada
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`rounded-full px-2 py-1 text-xs font-semibold ${
                      category.is_active
                        ? "bg-green-100 text-green-700"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {category.is_active ? "Ativa" : "Inativa"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!isLoading && rows.length === 0 && (
          <p className="p-6 text-center text-sm text-muted-foreground">
            Nenhuma categoria cadastrada.
          </p>
        )}
      </div>
    </div>
  );
}
